import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormControl
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { first } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { serializeDate } from '../../../core/helpers';
import { ClientField } from '../../../domain/models';
import { ClientService } from '../../../domain/services';
import { SelectionService } from '../../../selection/services';
import { PersonalInformationForm } from '../../models';

@Component({
    selector: 'app-personal-information-form',
    templateUrl: './personal-information-form.component.html',
    styleUrls: ['./personal-information-form.component.scss'],
    standalone: false
})
export class PersonalInformationFormComponent implements OnInit {
    personalInformation = PersonalInformationForm.initialize(this._fb);
    floatLabelControl = new UntypedFormControl('auto');
    ssnRequired = false;
    mobilePhoneRequired = false;
    homePhoneRequired = false;
    workPhoneRequired = false;
    emergencyPhoneRequired = false;
    emergencyContactHeader: string | undefined = '';

    hasSocialSecurityNumberSelections$ =
        this.selectionService.hasSocialSecurityNumberSelections$;
    sexSelections$ = this.selectionService.sexSelections$;
    sexBinarySelections$ = this.selectionService.sexBinarySelections$;
    genderIdentitySelections$ = this.selectionService.genderIdentitySelections$;
    pronounSelections$ = this.selectionService.pronounSelections$;
    canContactByTextSelections$ =
        this.selectionService.canContactByTextSelections$;
    contactPreferenceSelections$ =
        this.selectionService.contactPreferenceSelections$;
    stateSelections$ = this.selectionService.stateSelections$;
    computerEquipmentSelections$ =
        this.selectionService.computerEquipmentSelections$;

    residenceAreaSelections$ = this.selectionService.residenceAreaSelections$;

    @Input() submitting: boolean = false;

    @Input()
    set fields(value: ClientField[] | null) {
        if (value) {
            PersonalInformationForm.setFields(value, this.personalInformation);
            this.ssnRequired =
                value.find(
                    (field) => field.fieldName === 'SocialSecurityNumber'
                )?.isRequired ?? false;
            this.emergencyPhoneRequired =
                value.find(
                    (field) => field.fieldName === 'EmergencyContactPhone'
                )?.isRequired ?? false;
            this.mobilePhoneRequired =
                value.find((field) => field.fieldName === 'MobilePhone')
                    ?.isRequired ?? false;
            this.homePhoneRequired =
                value.find((field) => field.fieldName === 'HomePhone')
                    ?.isRequired ?? false;
            this.workPhoneRequired =
                value.find((field) => field.fieldName === 'WorkPhone')
                    ?.isRequired ?? false;
        }
    }

    @Input() // TODO: NO `any`
    set value(v: any) {
        // personalInfo returned
        if (v?.applicationId) {
            this.personalInformation.patchValue({ ...v });
            if (
                this.personalInformation.value.birthDate != undefined &&
                this.personalInformation.value.birthDate != null
            ) {
                const [year, month, day] =
                    this.personalInformation.value.birthDate
                        .split('-')
                        .map(Number);
                const safeDate = new Date(year, month - 1, day);
                this.personalInformation.controls['birthDate'].setValue(
                    safeDate
                );
            }
        }
    }

    @Output() submitted = new EventEmitter<any>();

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly snackBar: MatSnackBar,
        private readonly _transloco: TranslocoService,
        private readonly selectionService: SelectionService,
        private readonly _googleAnalyticsService: GoogleAnalyticsService,
        private readonly _client: ClientService
    ) {}

    ngOnInit() {
        this._client.latest.subscribe((client) => {
            this.emergencyContactHeader =
                client?.emergencyContactIntroductionHtml || undefined;
        });
    }

    get touched() {
        return this.personalInformation.touched;
    }

    get birthDate(): AbstractControl | null {
        return this.personalInformation.get('birthDate');
    }

    get ageAtEnrollment(): AbstractControl | null {
        return this.personalInformation.get('ageAtEnrollment');
    }

    get firstName(): AbstractControl | null {
        return this.personalInformation.get('firstName');
    }

    get middleName(): AbstractControl | null {
        return this.personalInformation.get('middleName');
    }

    get lastName(): AbstractControl | null {
        return this.personalInformation.get('lastName');
    }

    get suffix(): AbstractControl | null {
        return this.personalInformation.get('suffix');
    }

    get otherNames(): AbstractControl | null {
        return this.personalInformation.get('otherNames');
    }

    get hasSocialSecurityNumberSelectionId(): AbstractControl | null {
        return this.personalInformation.get(
            'hasSocialSecurityNumberSelectionId'
        );
    }

    get socialSecurityNumber(): AbstractControl | null {
        return this.personalInformation.get('socialSecurityNumber');
    }

    get sexSelectionId(): AbstractControl | null {
        return this.personalInformation.get('sexSelectionId');
    }

    get sexBinarySelectionId(): AbstractControl | null {
        return this.personalInformation.get('sexBinarySelectionId');
    }

    get genderIdentitySelectionId(): AbstractControl | null {
        return this.personalInformation.get('genderIdentitySelectionId');
    }

    get pronounsSelectionId(): AbstractControl | null {
        return this.personalInformation.get('pronounsSelectionId');
    }

    get canContactByTextSelectionId(): AbstractControl | null {
        return this.personalInformation.get('canContactByTextSelectionId');
    }

    get mobilePhone(): AbstractControl | null {
        return this.personalInformation.get('mobilePhone');
    }

    get contactPreferenceSelectionId(): AbstractControl | null {
        return this.personalInformation.get('contactPreferenceSelectionId');
    }

    get homePhone(): AbstractControl | null {
        return this.personalInformation.get('homePhone');
    }

    get workPhone(): AbstractControl | null {
        return this.personalInformation.get('workPhone');
    }

    get consentReceiveTextMessage(): AbstractControl | null {
        return this.personalInformation.get('consentReceiveTextMessage');
    }

    onConsentReceiveTextMessageChange(checked: boolean): void {
        const control = this.personalInformation.get(
            'consentReceiveTextMessage'
        );
        if (control) {
            control.setValue(checked ? 1 : null);
        }
    }

    get email(): AbstractControl | null {
        return this.personalInformation.get('email');
    }

    get secondaryEmail(): AbstractControl | null {
        return this.personalInformation.get('secondaryEmail');
    }

    get mailingAddress(): AbstractControl | null {
        // mailingAddress is a FormGroup (as opposed to a FormControl),
        // and it seems when you use FormGroup.get(<name-of-form-group>),
        // an error can be thrown if it doesn't find the form group inside
        // the parent form group (our dynamic fields may not have any
        // address fields needed for the form depending on the client, so
        // we don't add this formgroup until we know we need it).  But
        // our template is checking if its needed during change detection cycles,
        // so we use the contains method here.
        if (this.personalInformation.contains('mailingAddress'))
            return this.personalInformation.get('mailingAddress');
        else return null;
    }

    get streetAddress(): AbstractControl | null | undefined {
        return this.mailingAddress?.get('streetAddress');
    }

    get aptSuiteOther(): AbstractControl | null | undefined {
        return this.mailingAddress?.get('aptSuiteOther');
    }

    get city(): AbstractControl | null | undefined {
        return this.mailingAddress?.get('city');
    }

    get stateCode(): AbstractControl | null | undefined {
        return this.mailingAddress?.get('stateCode');
    }

    get postalCode(): AbstractControl | null | undefined {
        return this.mailingAddress?.get('postalCode');
    }

    get county(): AbstractControl | null | undefined {
        return this.mailingAddress?.get('county');
    }

    get residenceAreaCode(): AbstractControl | null | undefined {
        return this.personalInformation?.get('residenceAreaCode');
    }

    get emergencyContactName(): AbstractControl | null {
        return this.personalInformation.get('emergencyContactName');
    }

    get emergencyContactPhone(): AbstractControl | null {
        return this.personalInformation.get('emergencyContactPhone');
    }

    get emergencyContactRelationship(): AbstractControl | null {
        return this.personalInformation.get('emergencyContactRelationship');
    }

    get computerEquipmentSelectionIds(): AbstractControl | null {
        return this.personalInformation.get('computerEquipmentSelectionIds');
    }

    setAgeAtEnrollment(date: any) {
        let age = PersonalInformationForm.calculateAge(date);

        this.personalInformation.controls['ageAtEnrollment'].setValue(age);
    }

    onSubmit(event: any): void {
        this.personalInformation.markAllAsTouched();
        if (this.personalInformation.valid) {
            this.submitting = true;

            // Convert the birthDate Date object to a string representation of just the date
            this.personalInformation.value.birthDate = serializeDate(
                this.personalInformation.value.birthDate
            );

            this.submitted.next(this.personalInformation.value);
        } else {
            this._transloco
                .selectTranslate('application.snack.invalid-form')
                .pipe(first())
                .subscribe((message) =>
                    this.snackBar.open(message, undefined, {
                        duration: 4000
                    })
                );

            this._googleAnalyticsService.formError(this.personalInformation);
        }
    }
}
